import { useEffect, useState } from "react";
import { useAuthenticated, usePermissions } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Navigate } from "react-router";
import http from "../auth/http-common";

export interface ExtraModule {
  key: string;
  name: string;
  description: string;
  enabled?: boolean;
  adminPath?: string;
  appPath?: string;
}
export interface NetworkPermissions {
  role: string;
  network?: { id: string; value: string };
  modules: ExtraModule[];
}

export default function NetworkModules() {
  useAuthenticated();
  const { permissions, isLoading, refetch } =
    usePermissions<NetworkPermissions>();
  const system = permissions?.role === "SYSTEM_ADMIN";
  const [networks, setNetworks] = useState<{ id: string; value: string }[]>([]);
  const [networkId, setNetworkId] = useState("");
  const [catalog, setCatalog] = useState<ExtraModule[]>([]);
  const [assigned, setAssigned] = useState<ExtraModule[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!permissions) return;
    if (system) {
      Promise.all([
        http.get("/business-networks"),
        http.get("/admin/network-modules/catalog"),
      ])
        .then(([choices, modules]) => {
          setNetworks(choices.data);
          setCatalog(modules.data);
        })
        .catch(() => setError("Moduler og netværk kunne ikke indlæses."));
    } else setNetworkId(permissions.network?.id || "");
  }, [permissions, system]);

  useEffect(() => {
    let active = true;
    setAssigned([]);
    setLoaded(false);
    if (!networkId) return;
    setBusy(true);
    http
      .get(`/admin/network-modules/${networkId}`)
      .then((response) => {
        if (!active) return;
        setAssigned(response.data);
        setLoaded(true);
        if (!system && !response.data.length) refetch();
      })
      .catch(() => {
        if (active) setError("Moduler kunne ikke indlæses.");
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [networkId, system, refetch]);

  const change = async (
    module: ExtraModule,
    action: "assignment" | "activation",
    value: boolean,
  ) => {
    setBusy(true);
    setError("");
    try {
      const response = await http.put(
        `/admin/network-modules/${networkId}/${encodeURIComponent(
          module.key,
        )}/${action}`,
        action === "assignment" ? { assigned: value } : { enabled: value },
      );
      setAssigned(response.data);
      if (!system) await refetch();
    } catch {
      setError("Ændringen kunne ikke gemmes. Prøv igen.");
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <CircularProgress />;
  if (
    !system &&
    (!permissions?.modules?.length || (loaded && !assigned.length))
  )
    return <Navigate to="/" replace />;
  if (!system && busy && !loaded) return <CircularProgress />;
  const modules = system ? catalog : assigned;
  return (
    <Box sx={{ maxWidth: 960, p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {system ? "Modultildeling" : "Ekstra moduler"}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {system
          ? "Vælg et netværk, og tildel de ekstra moduler, det må bruge. Moduler kan derefter aktiveres på netværket."
          : `Aktivér eller deaktivér de moduler, der er tildelt ${
              permissions?.network?.value || "dit netværk"
            }.`}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {system && (
        <TextField
          select
          label="Erhvervsnetværk"
          value={networkId}
          onChange={(e) => setNetworkId(e.target.value)}
          disabled={busy}
          sx={{ minWidth: 260, mb: 3 }}
        >
          {networks.map((network) => (
            <MenuItem key={network.id} value={network.id}>
              {network.value}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!modules.length && (
        <Alert severity="info">
          {system
            ? "Der er endnu ingen ekstra moduler i kataloget."
            : "Der er ingen moduler tilgængelige for dit netværk."}
        </Alert>
      )}
      <Stack spacing={2}>
        {modules.map((module) => {
          const current = assigned.find((item) => item.key === module.key);
          return (
            <Card key={module.key} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                >
                  <Typography variant="h6">{module.name}</Typography>
                  <Chip
                    size="small"
                    label={
                      current?.enabled
                        ? "Aktivt"
                        : current
                        ? "Deaktiveret"
                        : "Ikke tildelt"
                    }
                    color={current?.enabled ? "success" : "default"}
                  />
                </Stack>
                <Typography color="text.secondary" sx={{ my: 2 }}>
                  {module.description}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Stack direction="row" alignItems="center">
                    <Switch
                      inputProps={{ "aria-label": `Aktivér ${module.name}` }}
                      checked={!!current?.enabled}
                      disabled={busy || !current}
                      onChange={(_, enabled) =>
                        change(module, "activation", enabled)
                      }
                    />
                    <Typography>Aktivér på netværket</Typography>
                  </Stack>
                  {system && (
                    <Button
                      disabled={busy || !networkId}
                      color={current ? "error" : "primary"}
                      onClick={() => change(module, "assignment", !current)}
                    >
                      {current ? "Fjern tildeling" : "Tildel modul"}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
